import { useEffect, useState } from 'react';

/**
 * SAB-959: a value that settles `delay` ms after it stops changing.
 *
 * ── WHY THE INPUT IS NOT DEBOUNCED, ONLY THE FILTER ─────────────────────────
 *
 * The text field stays fully controlled by the immediate state, so typing never lags.
 * Only the derived work — filtering ~2,200 players and re-rendering the list — waits for
 * this. Debouncing the input itself is the version of this change that feels broken:
 * characters appear late on a phone keyboard, which is worse than the list being 150ms
 * behind.
 *
 * ── LEADING EDGE ON CLEAR ───────────────────────────────────────────────────
 *
 * Emptying the query resolves immediately rather than after the delay. Clearing a search
 * is how someone gets the full pool back, often on a clock, and making that wait feels
 * like the button did not work. Only narrowing is deferred.
 */
export function useDebouncedValue<T>(value: T, delay: number, immediateWhen?: (v: T) => boolean): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    if (immediateWhen?.(value)) {
      setSettled(value);
      return;
    }
    const t = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(t);
    // `immediateWhen` is intentionally not a dependency: callers pass an inline predicate,
    // which would be a new function every render and restart the timer on each keystroke —
    // debouncing nothing while looking like it worked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, delay]);

  return settled;
}
